<div class="flex min-h-full flex-col" [class.bg-vw-bg]="autoNavigate">
  <!-- En-tête des sous-pages. Page routée : il réserve la zone sûre ; en usage intégré
       (assistant de zone, Transplanter), le parent la gère et le retour lui revient. -->
  <vw-page-header [safeArea]="autoNavigate" [inset]="!autoNavigate" [backLabel]="backLabel"
    [title]="(editing ? 'garden.beds.create.title-edit' : 'garden.beds.create.title') | translate"
    [subtitle]="presetZoneName ? ('garden.beds.create.zone' | translate) + ' ' + presetZoneName : null"
    (back)="back()"></vw-page-header>

  <!-- Marge basse à la hauteur de la barre d'action (plus haute avec l'encart Premium) : l'aperçu ne passe jamais dessous. -->
  <div class="flex flex-col gap-5" [class.px-4]="autoNavigate" [class.pb-40]="!premiumMessageKey" [class.pb-64]="!!premiumMessageKey">
    @if (loadError) {
      <p role="alert" class="rounded-field bg-red-50 p-3 text-body-s text-vw-danger">{{ "errors.businessException" | translate }}</p>
    }

    <!-- Comment tu la cultives ? -->
    <div class="flex flex-col gap-2">
      <p class="text-body-s text-vw-muted" id="bed-kind-label">{{ "garden.beds.create.kind.question" | translate }}</p>
      <vw-choice-selector variant="list" [large]="true" [options]="typeOptions" [value]="type"
        [ariaLabel]="'garden.beds.create.kind.question' | translate" (valueChange)="selectType($event)"></vw-choice-selector>
    </div>

    <!-- Zone (dropdown si non imposée) -->
    @if (!presetZoneName) {
      <div class="flex flex-col gap-1">
        <label class="text-body-s text-vw-muted">{{ "garden.beds.create.zone" | translate }}</label>
        <vw-dropdown [options]="zones" [placeholder]="'garden.beds.create.zone-placeholder' | translate"
          [(ngModel)]="zone" (ngModelChange)="onZoneChange()"></vw-dropdown>
      </div>
    }

    @if (isRectangular) {
      <!-- Largeur et longueur, en cm -->
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-body-s text-vw-muted" for="bed-width">{{ "garden.beds.create.width" | translate }}</label>
          <vw-text-input inputId="bed-width" type="text" inputMode="decimal" [(ngModel)]="widthCm"
            [isError]="widthError" [suffix]="widthUnit"></vw-text-input>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-body-s text-vw-muted" for="bed-length">{{ "garden.beds.create.length" | translate }}</label>
          <vw-text-input inputId="bed-length" type="text" inputMode="decimal" [(ngModel)]="lengthCm"
            [isError]="lengthError" [suffix]="lengthUnit"></vw-text-input>
        </div>
        <!-- « 1,2 » se lit en mètres : l'unité le dit -->
        <ng-template #widthUnit><span class="text-body text-vw-muted" data-testid="width-unit">{{ unitOf(widthCm) }}</span></ng-template>
        <ng-template #lengthUnit><span class="text-body text-vw-muted">{{ unitOf(lengthCm) }}</span></ng-template>
      </div>
      @if (gridSuggestion; as hint) {
        <!-- Trop de cases : la case qui tiendrait, ou les rangs (lot 2) -->
        <div class="flex flex-col gap-2 rounded-field bg-vw-warn-bg p-3" role="alert" data-testid="grid-too-large">
          <p class="text-body-s text-vw-warn-ink">
            @if (hint.cellCm) {
              {{ "garden.beds.create.grid-suggest" | translate: { size: hint.cellCm } }}
            } @else {
              {{ "garden.beds.create.grid-too-large" | translate }}
            }
          </p>
          @if (hint.cellCm) {
            <button type="button" class="min-h-target self-start text-body-s font-semibold text-vw-primary" data-testid="use-cell-size"
              (click)="useCellSize(hint.cellCm)">{{ "garden.beds.create.grid-use" | translate: { size: hint.cellCm } }}</button>
          }
        </div>
      } @else if (!dimensionsValid) {
        <p role="alert" class="-mt-3 text-body-s text-vw-danger" data-testid="dimensions-error">
          {{ "garden.beds.create.dimensions-range" | translate: { min: 10, max: maxSideCm } }}
        </p>
      } @else if (cutsPlanting; as cut) {
        <p role="alert" class="-mt-3 text-body-s text-vw-danger" data-testid="dimensions-cut">
          {{ (cut.name ? "garden.beds.create.dimensions-cut" : "garden.beds.create.dimensions-cut-unnamed") | translate: cut }}
        </p>
      } @else if (dimensionsAssumed) {
        <p class="-mt-3 text-body-s text-vw-muted" data-testid="dimensions-assumed">
          {{ "garden.beds.create.dimensions-assumed" | translate }}
        </p>
      }
      @if (tooWide) {
        <p class="rounded-field bg-vw-warn-bg p-3 text-body-s text-vw-warn-ink" role="status" data-testid="too-wide">
          {{ "garden.beds.create.too-wide" | translate }}
        </p>
      }

      <!-- Aperçu : ce que donne la planche -->
      @if (dimensionsValid) {
        <div class="flex flex-col gap-3 rounded-card bg-vw-surface-tint p-4" data-testid="bed-preview">
          <div>
            <p class="text-title-s text-vw-primary-ink">
              {{ "garden.beds.create.preview-title" | translate: { width: widthLabel, length: lengthLabel } }}
            </p>
            <p class="text-body-s text-vw-primary-ink">
              {{ "garden.beds.create.preview-cells" | translate: { count: gridCellCount, size: cellCm } }}
            </p>
          </div>
          <div class="grid gap-1" [style.grid-template-columns]="'repeat(' + columns + ', minmax(0, 1fr))'" aria-hidden="true">
            @for (c of previewCells; track $index) {
              <div class="aspect-square rounded border border-dashed border-primary-300 bg-vw-surface"></div>
            }
          </div>
        </div>
      }

      <!-- Réglages de la planche : la taille des cases -->
      @if (editing) {
        <div class="flex flex-col gap-2">
          <p class="text-body-s text-vw-muted">{{ "garden.beds.create.cell-size" | translate }}</p>
          <vw-choice-selector [options]="cellSizePills" [(value)]="cellCm" variant="chips"
            [ariaLabel]="'garden.beds.create.cell-size' | translate"></vw-choice-selector>
          <p class="text-caption text-vw-muted">{{ "garden.beds.create.cell-size-hint" | translate }}</p>
        </div>
      }
    } @else {
      <!-- Pot : un nombre de places -->
      <div class="flex flex-col gap-3 rounded-card border border-vw-line bg-vw-surface p-4">
        <div>
          <p class="text-body font-semibold text-vw-ink">{{ "garden.beds.create.capacity" | translate }}</p>
          <p class="text-caption text-vw-muted">{{ "garden.beds.create.capacity-hint" | translate }}</p>
        </div>
        <vw-quantity-stepper [(value)]="capacity" [min]="1" [max]="50"></vw-quantity-stepper>
        <p class="text-caption text-vw-muted">{{ "garden.beds.create.pot-preview-hint" | translate }}</p>
      </div>
    }

    <!-- Nom -->
    <div class="flex flex-col gap-1">
      <label class="text-body-s text-vw-muted" for="bed-name">
        {{ (isRectangular ? "garden.beds.create.name" : "garden.beds.create.name-pot") | translate }} *
      </label>
      <vw-text-input inputId="bed-name" type="text" [placeholder]="'garden.beds.create.name-placeholder' | translate"
        [(ngModel)]="name" (ngModelChange)="onNameChange()" [isError]="nameError"></vw-text-input>
    </div>

    <!-- Caractéristiques (facultatif) -->
    <div class="rounded-card border border-vw-line bg-vw-surface p-4">
      <h2 class="mb-3 text-body font-semibold text-vw-ink">
        {{ "garden.beds.create.characteristics" | translate }}
        <span class="text-caption font-normal text-vw-muted">({{ "garden.beds.create.optional" | translate }})</span>
      </h2>

      <p class="mb-2 text-body-s text-vw-muted">{{ "garden.beds.create.exposure" | translate }}</p>
      <div class="mb-4">
        <vw-choice-selector [ariaLabel]="'garden.beds.create.exposure' | translate" [options]="exposurePills"
          [(value)]="exposure" variant="chips" [deselectable]="true"></vw-choice-selector>
      </div>

      <p class="mb-2 text-body-s text-vw-muted">{{ "garden.beds.create.orientation" | translate }}</p>
      <div class="mb-4">
        <vw-choice-selector [ariaLabel]="'garden.beds.create.orientation' | translate" [options]="orientationPills"
          [(value)]="orientation" variant="chips" [deselectable]="true"></vw-choice-selector>
      </div>

      @if (isRectangular) {
        <p class="mb-2 text-body-s text-vw-muted">{{ "garden.beds.create.soil" | translate }}</p>
        <vw-choice-selector [ariaLabel]="'garden.beds.create.soil' | translate" [options]="soilPills"
          [(value)]="soilType" variant="chips" [deselectable]="true"></vw-choice-selector>
      }
    </div>

    @if (!editing) {
      <p class="text-center text-caption text-vw-muted">{{ "garden.beds.create.editable-later" | translate }}</p>
    }

    @if (errorKey) {
      <p role="alert" class="rounded-field bg-red-50 p-3 text-body-s text-vw-danger" data-testid="bed-error">{{ errorKey | translate }}</p>
    }
  </div>

  <!-- Valider -->
  <div class="fixed bottom-0 left-0 right-0 flex flex-col gap-3 border-t border-vw-line bg-vw-surface px-4 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))]"
    data-testid="action-bar">
    <!-- Refus premium : invitation en contexte, jamais une erreur rouge. Dans la barre, juste au-dessus
         du bouton touché : posée après le formulaire, elle tombait sous la barre (revue design P3.1.6.3). -->
    @if (premiumMessageKey) {
      <vw-premium-teaser
        [compact]="true"
        [message]="premiumMessageKey | translate"
        [detail]="'premium.teaser.beds-detail' | translate"
        [source]="'bed_limit'"
        (ctaClicked)="openPaywall()"
      />
    }
    <!-- Jamais grisé : touché alors qu'il manque quelque chose, il dit quoi, juste au-dessus. -->
    @if (submitAttempted && missingKey; as missing) {
      <p role="alert" class="text-body-s text-vw-danger" data-testid="bed-missing">{{ missing | translate }}</p>
    }
    <div class="flex gap-3">
      <vw-button class="flex-1" [look]="'secondary'" customClass="w-full" label="common.cancel" (onClick)="back()"></vw-button>
      <vw-button class="flex-1" data-testid="submit" customClass="w-full"
        [label]="editing ? 'garden.beds.create.save' : (isRectangular ? 'garden.beds.create.submit' : 'garden.beds.create.submit-pot')"
        [look]="'primary'" [loading]="submitLoading" (onClick)="submit()"></vw-button>
    </div>
  </div>
</div>

<vw-premium-toast
  [isOpen]="premiumPaywallOpen"
  [source]="'bed_limit'"
  (close)="premiumPaywallOpen = false"
></vw-premium-toast>
